import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileUp, Loader2, Paperclip, Send, ShieldAlert } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { api, errMsg } from "@/lib/api";
import { NameHistory, validateNames } from "@/components/wizard/NameHistory";
import { useSpanish } from "@/lib/translate";

export default function ProvideInfo() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState("");
  const [names, setNames] = useState<any>(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<{ id: string; filename: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useSpanish(info?.lang === "es");
  useEffect(() => {
    api.get(`/public/info-request/${token}`).then((r) => { setInfo(r.data); if (r.data.names) setNames({ ...r.data.names, marriages: r.data.names.marriages || [] }); })
      .catch((e) => setErr(errMsg(e)));
  }, [token]);
  const upload = async (list: FileList) => {
    setUploading(true); setErr("");
    for (const f of Array.from(list)) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post(`/public/info-request/${token}/upload`, fd);
        setFiles((x) => [...x, data]);
      } catch (e) { setErr(errMsg(e)); }
    }
    setUploading(false);
    if (ref.current) ref.current.value = "";
  };
  const send = async () => {
    setErr("");
    if (names) { const v = validateNames(names); if (v) { setErr(v); return; } }
    setBusy(true);
    try { await api.post(`/public/info-request/${token}/respond`, { reply, files: files.map((f) => f.id), names }); setDone(true); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  return (
    <PublicShell>
      <div className="mx-auto max-w-2xl px-4 py-14">
        <div className="card-soft fade-up p-7 sm:p-10" data-testid="provide-card">
          <h1 className="text-3xl font-extrabold tracking-tight">Send the information we need</h1>
          {!info && !err && <Loader2 className="mt-6 h-6 w-6 animate-spin text-[#94A3B8]" />}
          {done ? (
            <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#DCFCE7] px-4 py-3 text-[15px] text-[#166534]" data-testid="provide-done">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> Thank you! We received your information and will continue your background screening.
            </div>
          ) : info && (info.closed ? (
            <p className="mt-6 rounded-xl bg-[#F1F5F9] px-4 py-3 text-[15px]" data-testid="provide-closed">This request is closed or the link has expired. If we still need something, please reply to our email.</p>
          ) : (
            <div className="mt-5 space-y-6">
              <p className="text-[15px] text-[#475569]">Hi {info.first_name}, to finish your screening{info.org_name ? ` for ${info.org_name}` : ""} we need:</p>
              {info.items?.length > 0 && <ul className="list-disc space-y-1 pl-6 text-[15px] font-semibold" data-testid="provide-items">{info.items.map((i: any) => <li key={i.key}>{i.label}</li>)}</ul>}
              {info.message && <div className="whitespace-pre-wrap rounded-xl border-l-4 border-[#F26B4F] bg-white px-4 py-3 text-[15px]" data-testid="provide-message">{info.message}</div>}
              {names && <div><h2 className="mb-2 text-base font-bold text-[#1B365D]">Your name history</h2><NameHistory p={names} onChange={setNames} /></div>}
              <div>
                <h2 className="mb-2 text-base font-bold text-[#1B365D]">Upload documents (optional)</h2>
                <input ref={ref} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => e.target.files && upload(e.target.files)} data-testid="provide-file-input" />
                <button type="button" className="btn-secondary h-12 w-full" disabled={uploading || files.length >= 10} onClick={() => ref.current?.click()} data-testid="provide-upload-button">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Choose PDF, JPG or PNG files (up to 15 MB each)
                </button>
                {files.map((f) => <p key={f.id} className="mt-2 flex items-center gap-2 text-sm text-[#166534]" data-testid="provide-uploaded"><Paperclip className="h-4 w-4" /> {f.filename}</p>)}
                <p className="mt-2 text-xs text-[#94A3B8]">Please don't upload or type your Social Security number here. If we need it, we'll ask you to call.</p>
              </div>
              <label className="block"><span className="field-label">Message (optional)</span>
                <textarea className="field-input min-h-[100px] py-3" maxLength={4000} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="provide-reply" /></label>
              <button className="btn-primary h-14 w-full text-lg" disabled={busy || uploading} onClick={send} data-testid="provide-send-button">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5" /> Send to InfoRetrieval</>}
              </button>
            </div>
          ))}
          {err && <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#FEE2E2] px-4 py-3 text-[15px] text-[#991B1B]" data-testid="provide-error"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> {err}</div>}
        </div>
      </div>
    </PublicShell>
  );
}
